import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { useResponsive } from '../../context/ResponsiveContext';
import { cn } from '../../lib/utils';

/**
 * Touch-friendly month picker used in layout header and mobile overflow menus.
 * On touch mobile/tablet, opens a bottom sheet instead of a popover select.
 */
export default function MonthSelect({
  value,
  onValueChange,
  months,
  id = 'layout-month-select',
  triggerClassName,
  touchFriendly = false,
}) {
  const { isMobile, isTablet, isTouchDevice } = useResponsive();
  const useMobileSheet = touchFriendly || (isTouchDevice && (isMobile || isTablet));
  const [open, setOpen] = useState(false);

  const selectedMonth = months.find((m) => m.key === value);
  const displayLabel = selectedMonth?.label || 'Select month';

  const handleSelect = (monthKey) => {
    onValueChange(monthKey);
    setOpen(false);
  };

  const triggerClasses = cn(
    'dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100',
    useMobileSheet
      ? 'h-11 min-h-[44px] w-full text-ds-body'
      : 'h-11 min-h-[44px] w-[7.5rem] flex-shrink-0 text-base md:w-[8.125rem] md:text-ds-body lg:h-8 lg:min-h-8 lg:w-[8.75rem]',
    triggerClassName
  );

  if (useMobileSheet) {
    return (
      <>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Select month to view"
          className={cn('justify-between font-normal', triggerClasses)}
          onClick={() => setOpen(true)}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[min(70dvh,480px)] rounded-t-xl p-0 dark:bg-gray-900"
          >
            <SheetHeader className="border-b border-border px-3 py-2 text-left max-md:px-3 max-md:py-2 md:px-4">
              <SheetTitle>Select month</SheetTitle>
            </SheetHeader>
            <ul
              role="listbox"
              aria-label="Available months"
              className="overflow-y-auto p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] touch-pan-y max-md:p-1.5"
            >
              {months.map((month) => {
                const isSelected = value === month.key;
                return (
                  <li key={month.key} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(month.key)}
                      className={cn(
                        'flex w-full min-h-[44px] items-center rounded-sm px-3 py-2.5 text-left text-ds-body',
                        'hover:bg-accent dark:hover:bg-gray-800',
                        isSelected && 'bg-accent/50 dark:bg-gray-800'
                      )}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="dark:text-gray-100">{month.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        id={id}
        aria-label="Select month to view"
        className={triggerClasses}
      >
        <SelectValue placeholder="Select month" />
      </SelectTrigger>
      <SelectContent
        className="dark:bg-gray-800 dark:border-gray-700 max-h-[min(60vh,320px)]"
        sideOffset={11}
        collisionPadding={8}
      >
        {months.map((month) => (
          <SelectItem
            key={month.key}
            value={month.key}
            className="dark:text-gray-100 dark:hover:bg-gray-700"
          >
            {month.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
